import { CalendarDays, Clock, Info, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Clinic } from "@fysaro/domain";
import { Button } from "@/design-system/components/ui/button";
import { Card } from "@/design-system/components/ui/card";
import { WeeklyScheduleView } from "../tenant/clinics/ui/components/WeeklyScheduleView";
import { formatShortDayMonth } from "@fysaro/shared";
import type { WeeklySchedule } from "@fysaro/types";

export function PublicClinicOverview({ clinic }: { clinic: Clinic }) {
  const { t, i18n } = useTranslation(["common", "clinic", "clinic-settings"]);

  console.log(i18n.language);

  // 1. Helpers de localización externos
  const openInGoogleMaps = () => {
    const baseUrl = "https://www.google.com/maps/search/?api=1&query=";
    const query =
      clinic.latitude && clinic.longitude
        ? `${clinic.latitude},${clinic.longitude}`
        : encodeURIComponent(
            clinic.formattedAddress || clinic.address || clinic.name,
          );
    window.open(`${baseUrl}${query}`, "_blank", "noopener,noreferrer");
  };

  const baseEmbedUrl = "https://maps.google.com/maps";
  const embedQuery =
    clinic?.latitude && clinic?.longitude
      ? `?q=${clinic.latitude},${clinic.longitude}`
      : `?q=${encodeURIComponent(clinic?.formattedAddress || clinic?.address || clinic?.name)}`;
  const mapEmbedUrl = `${baseEmbedUrl}${embedQuery}&z=15&output=embed`;

  // const formatDate = (dateStr: string) => {
  //   try {
  //     // Si viene como "12-25", le acoplamos el año actual delante: "2026-12-25"
  //     const currentYear = new Date().getFullYear();
  //     const normalizedDate = dateStr.includes("-") && dateStr.length === 5
  //       ? `${currentYear}-${dateStr}`
  //       : dateStr;

  //     const date = new Date(normalizedDate);

  //     // Si aun así­ el parseo falla, tiramos al catch
  //     if (isNaN(date.getTime())) return dateStr;

  //     return date.toLocaleDateString(undefined, {
  //       day: "numeric",
  //       month: "short", // Mostrará "dic", "ene", "ago", etc.
  //     });
  //   } catch {
  //     return dateStr; // Salvavidas: si falla, muestra el string original sin romper la pantalla
  //   }
  // };
  console.log(i18n.language, "----------------------------");
  return (
    <div className="space-y-6">
      {/* 📊 FILA 1: MAPA INTERACTIVO (2/3) + BLOQUES INFORMATIVOS (1/3) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        {/* 🗺️ EL MAPA (Ocupa 2 columnas en Desktop) */}
        <Card className="md:col-span-2 border border-border/40 shadow-sm overflow-hidden rounded-2xl min-h-[350px] md:min-h-full relative flex flex-col justify-end">
          <div className="absolute inset-0 w-full h-full bg-muted">
            <iframe
              title={t("clinic:details.clinic_map")}
              src={mapEmbedUrl}
              className="w-full h-full opacity-85 dark:opacity-50"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
            />
            <div className="absolute inset-0 pointer-events-none md:pointer-events-auto md:hidden" />
          </div>

          {/* Flotante con dirección estructural */}
          <div className="relative z-10 p-4 md:p-6 w-full md:max-w-md md:ml-6 md:mb-6">
            <div className="bg-background/95 backdrop-blur-md p-5 rounded-xl shadow-xl border border-border/40 text-center md:text-left space-y-2">
              <div className="flex items-center gap-2 justify-center md:justify-start">
                <MapPin className="w-4 h-4 text-primary shrink-0" />
                <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">
                  {t("clinic:details.how_to_get_there")}
                </span>
              </div>
              <p className="text-sm font-bold text-foreground tracking-tight leading-snug">
                {clinic?.address || clinic?.formattedAddress}
              </p>
              <p className="text-xs text-muted-foreground font-medium">
                {clinic?.city}
                {clinic?.zipCode ? `, ${clinic.zipCode}` : ""}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full md:w-auto rounded-xl mt-2 text-xs font-bold hover:bg-primary hover:text-primary-foreground transition-all shadow-sm"
                onClick={openInGoogleMaps}
              >
                <MapPin className="w-3 h-3 mr-2 shrink-0" />
                {t("clinic:details.view_in_google_maps")}
              </Button>
            </div>
          </div>
        </Card>

        {/* ⚙️ COLUMNA DE METADATOS (Ocupa 1 columna en Desktop y se apila verticalmente) */}
        <div className="md:col-span-1 flex flex-col gap-4">
          {/* BLOQUE: POLÍTICAS DE RESERVA */}
          <section className="bg-primary/5 border border-primary/10 rounded-2xl p-5 text-left flex-1 flex flex-col justify-center space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-xl text-primary shrink-0">
                <Info size={16} />
              </div>
              <h3 className="text-[10px] font-black uppercase tracking-widest text-primary/70">
                {t("clinic-settings:tab_calendar.public.labels.policies")}
              </h3>
            </div>
            <ul className="space-y-2 text-xs text-muted-foreground font-semibold">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                {t(
                  "clinic-settings:tab_calendar.public.labels.policies_future",
                  { days: clinic.calendarConf?.maxFutureDays },
                )}
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                {t(
                  "clinic-settings:tab_calendar.public.labels.policies_hours",
                  { hours: clinic.calendarConf?.minNoticeHours },
                )}
              </li>
            </ul>
          </section>

          {/* BLOQUE: DURACIÓN DE SESIONES */}
          <section className="bg-card border border-border/40 rounded-2xl p-5 shadow-sm text-left flex-1 flex flex-col justify-center space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-xl text-primary shrink-0">
                <Clock size={16} />
              </div>
              <h3 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                {t(
                  "clinic-settings:tab_calendar.public.labels.durations_title",
                )}
              </h3>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {clinic.calendarConf?.slotInterval && (
                <span className="px-3 py-1.5 bg-muted/60 border border-border/40 rounded-xl text-[10px] font-bold text-foreground">
                  {t("clinic-settings:tab_calendar.public.labels.durations", {
                    slotInterval: clinic.calendarConf.slotInterval,
                  })}{" "}
                  {t("common:durations.short_min")}
                </span>
              )}
            </div>
          </section>

          {/* BLOQUE: PRÓXIMOS FESTIVOS */}
          <section className="bg-card border border-border/40 rounded-2xl p-5 shadow-sm text-left flex-1 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-orange-500/10 rounded-xl text-orange-500 shrink-0">
                <CalendarDays size={16} />
              </div>
              <h3 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                {t(
                  "clinic-settings:tab_calendar.public.labels.holidays_section",
                )}
              </h3>
            </div>
            {clinic.calendarConf?.holidays &&
            clinic.calendarConf.holidays.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 max-h-[80px] overflow-y-auto pr-1 scrollbar-none">
                {clinic.calendarConf.holidays.map((h) => (
                  <div
                    key={h}
                    className="px-2.5 py-1 bg-muted/50 rounded-lg text-[10px] font-bold border border-border/30"
                  >
                    {formatShortDayMonth(h, i18n.language)}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground font-medium">
                {t(
                  "clinic-settings:tab_calendar.public.labels.holidays_section_empty",
                )}
              </p>
            )}
          </section>
        </div>
      </div>

      {/* 📅 FILA 2: HORARIO SEMANAL DETALLADO (Ancho completo debajo del mapa) */}
      <section className="bg-card border border-border/40 rounded-2xl md:rounded-2xl p-5 md:p-6 shadow-sm">
        <div className="flex flex-row items-center justify-between gap-4 mb-4">
          <h3 className="text-xs font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2 text-left">
            <CalendarDays size={16} className="text-primary/70 shrink-0" />
            {t("clinic-settings:tab_calendar.labels.weekly_schedule_section")}
          </h3>
        </div>
        <WeeklyScheduleView
          schedule={clinic?.calendarConf?.weeklySchedule as WeeklySchedule}
        />
      </section>
    </div>
  );
}
